import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, Subscription } from 'react-apollo';
import Box from '@material-ui/core/Box';
import { GET_TODOS } from 'graphql/todos';
import TodoCard from '../TodoCard';
import moment from 'moment';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Virtuoso } from 'react-virtuoso';
import Spin from 'components/common/Spin';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 220px)',
    minWidth: 250,
    backgroundColor: '#F1F1F1',
    borderRadius: 6,
  },
  header: {
    padding: theme.spacing(1),
  },
  title: {
    flex: 1,
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: 1,
  },
}));

const HeightPreservingItem = React.memo(({ children, ...props }) => {
  const [size, setSize] = useState(0);
  const knownSize = props['data-known-size'];
  useEffect(() => {
    setSize((prevSize) => {
      return knownSize == 0 ? prevSize : knownSize;
    });
  }, [knownSize]);
  // check style.css for the height-preserving-container rule
  return (
      <div
          {...props}
          className="height-preserving-container"
          style={{
            '--child-height': `${size}px`,
          }}
      >
        {children}
      </div>
  );
});

const TodoList = ({
  status,
  label,
  query,
  onDelete,
  sendNotification,
  markDone,
  onEdit,
}) => {
  const user = useSelector((s) => s.user.user);
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box display="flex" alignItems="center" className={classes.header}>
        <Typography className={classes.title}>{label}</Typography>
      </Box>
      <Subscription
        subscription={GET_TODOS}
        variables={{
          since: moment()
            .subtract(3, 'month')
            .format('YYYY-MM-DD'),
          status,
          query,
        }}
      >
        {({ data, loading, error }) => {
          if (error) return error.message;
          if (loading) return <Spin spinning />;
          const { todos } = data;

          return (
            <Droppable
              droppableId={status}
              mode="virtual"
              renderClone={(provided, snapshot, rubric) => {
                const todo = todos[rubric.source.index];
                return (
                  <TodoCard
                    provided={provided}
                    onDelete={onDelete}
                    sendNotification={sendNotification}
                    markDone={markDone}
                    onEdit={onEdit}
                    user={user}
                    data={todo}
                    key={todo.id}
                  />
                );
              }}
            >
              {(provided) => (
                <Virtuoso
                  components={{
                    Item: HeightPreservingItem,
                  }}
                  scrollerRef={provided.innerRef}
                  data={todos}
                  itemContent={(index, item) => (
                    <Draggable draggableId={String(item.id)} index={index}>
                      {(provided) => (
                        <Box px={1}>
                          <TodoCard
                              index={index}
                              onDelete={onDelete}
                              sendNotification={sendNotification}
                              markDone={markDone}
                              onEdit={onEdit}
                              user={user}
                              data={item}
                              key={item.id}
                              provided={provided}
                          />
                        </Box>
                      )}
                    </Draggable>
                  )}
                />
              )}
            </Droppable>
          );
        }}
      </Subscription>
    </Box>
  );
};

export default TodoList;
